// CRM 2.0 · M12 Template Builder · Buyer card (DC-70, DC-71) on Map fields & edit.
// Repeat for each applicant (1–4): the green buyer block (and signature lines) print once per applicant in booking
// order; Legal's typed co-applicant copy is struck through (not printed). The 1 / 2 / 3 / 4 switch shows the block
// repeated on the page and sets how many sample applicants the Preview PDF prints.
// Keep as typed (2): Applicant 1 and Applicant 2 are Legal's own paragraphs; 3 or 4 applicants cannot use it.
// "Space for each applicant" (buyer block, signature lines): empty lines above (default) or below every applicant's
// copy, e.g. room for a photo; shown on the page too.
import React, { useEffect, useState } from "react";
import cls from "./Templates.module.css";

export const BLOCK_NAMES = {
  REPEAT: {
    BUYER: "Buyer details",
    BUYER_COPY: "Typed copy of the buyer (not printed)",
    SIGNATURE: "Signatures",
    SIGNATURE_COPY: "Typed copy of the signatures (not printed)",
  },
  KEEP_TYPED: {
    BUYER: "Applicant 1",
    BUYER_COPY: "Applicant 2",
    SIGNATURE: "Signature · applicant 1",
    SIGNATURE_COPY: "Signature · applicant 2",
  },
};

const KEYS = ["BUYER", "BUYER_COPY", "SIGNATURE", "SIGNATURE_COPY"];

function label(text, extra) {
  const d = document.createElement("div");
  d.className = `wp-blk-label${extra ? ` ${extra}` : ""}`;
  d.textContent = text;
  return d;
}

// a look-only copy of the block for applicant k (no anchors, no blanks to click)
function cloneFor(p, k, isSignature) {
  const c = p.cloneNode(true);
  c.classList.add("wp-clone");
  ["data-anchor", "data-block", "data-edit"].forEach((a) => c.removeAttribute(a));
  c.querySelectorAll("[data-blank], [data-edit]").forEach((x) => { x.removeAttribute("data-blank"); x.removeAttribute("data-edit"); });
  if (isSignature) {
    const first = c.querySelector("[data-o]");
    if (first && /^\s*1(?=\s*[).])/.test(first.textContent)) first.textContent = first.textContent.replace(/^(\s*)1/, `$1${k}`);
  }
  return c;
}

function BuyerCard({ card, canEdit, onChange, sheetRef, page, sheetKey, shown, setShown, blanks }) {
  const repeat = card.buyerMode === "REPEAT";
  const itemOf = (k) => card.items.find((i) => i.blockKey === k);

  useEffect(() => { if (!repeat && shown > 2) setShown(2); }, [repeat, shown, setShown]);

  // labels above the blocks and, in Repeat mode, the block shown again for applicants 2 … n
  useEffect(() => {
    const timer = setTimeout(() => {
      const sheet = sheetRef.current;
      if (!sheet) return;
      sheet.querySelectorAll(".wp-blk-label, .wp-clone, .wp-gap, .wp-signline").forEach((el) => el.remove());
      sheet.querySelectorAll(".wp-lead").forEach((el) => { el.classList.remove("wp-lead"); el.removeAttribute("title"); });
      const group = (key) => [...sheet.querySelectorAll(`p[data-block="${key}"]`)];
      ["BUYER", "SIGNATURE"].forEach((key) => {
        const ps = group(key);
        if (!ps.length) return;
        const name = key === "BUYER" ? "buyer details" : "signatures";
        const blk = itemOf(key) || {};
        // the lines above the signature lines printed once with the 1st applicant (leadLines): applicant 1 starts there
        const all = [...sheet.querySelectorAll("p[data-anchor]")];
        const at = all.indexOf(ps[0]);
        const lead = repeat && key === "SIGNATURE" && blk.leadLines ? all.slice(Math.max(0, at - blk.leadLines), at) : [];
        lead.forEach((p) => { p.classList.add("wp-lead"); p.title = "Printed once, with the 1st applicant"; });
        // a line for the signature on the right of each applicant's line (signLine); the copies get it too
        if (repeat && key === "SIGNATURE" && blk.signLine) {
          const s = document.createElement("span");
          s.className = "wp-signline";
          s.title = "Signature";
          ps[0].appendChild(s);
        }
        (lead[0] || ps[0]).before(label(repeat ? `Buyer 1 · ${name}${lead.length ? " (the lines above print once, with buyer 1)" : ""}, repeats for each buyer` : `Buyer 1 · ${name} as typed`));
        if (!repeat) return;
        // the empty lines with each applicant's copy (Space for each applicant): above the name (default) or below
        const gapItem = itemOf(key) || {};
        const gap = Number(gapItem.gapLines) || 0;
        const above = gapItem.gapWhere !== "BELOW";
        const addGap = (at) => {
          let last = at;
          for (let g = 0; g < gap; g++) {
            const e = document.createElement("p");
            e.className = "wp-gap";
            e.textContent = " ";
            if (g === 0) e.setAttribute("data-gap", `${gap} empty line${gap > 1 ? "s" : ""} ${above ? "above" : "below"} each applicant`);
            last.after(e);
            last = e;
          }
          return last;
        };
        // above: between each "Applicant n" label and its copy; below: after the copy
        if (above) addGap((lead[0] || ps[0]).previousElementSibling);
        let after = above ? ps[ps.length - 1] : addGap(ps[ps.length - 1]);
        for (let k = 2; k <= shown; k++) {
          const l = label(`Buyer ${k} · the same ${name}, repeated`);
          after.after(l);
          after = above ? addGap(l) : l;
          for (const p of ps) {
            const c = cloneFor(p, k, key === "SIGNATURE");
            after.after(c);
            after = c;
          }
          if (!above) after = addGap(after);
        }
      });
      ["BUYER_COPY", "SIGNATURE_COPY"].forEach((key) => {
        const ps = group(key);
        if (!ps.length) return;
        const what = key === "BUYER_COPY" ? "buyer" : "signature lines";
        ps[0].before(label(repeat ? "Typed in Word · not printed (the part above repeats for each buyer)" : `Buyer 2 · ${what} as typed`, "copy"));
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [page, sheetKey, shown, repeat, sheetRef, blanks, card]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = [1, 2, 3, 4];
  const [open, setOpen] = useState(false);
  // folded: one line with what is marked and the 1-4 buyers switch; "Change" opens all the settings
  if (!open) {
    const sig = itemOf("SIGNATURE");
    return (
      <div className={cls.buyerSum}>
        <span><b>Buyer details:</b> <span className={itemOf("BUYER") ? cls.ok : cls.bad}>{itemOf("BUYER") ? "✓ marked" : "not marked yet"}</span></span>
        <span><b>Signatures:</b> <span className={sig ? cls.ok : cls.muted}>{sig ? `✓ marked${Number(sig.gapLines) ? ` · photo space ${sig.gapLines} lines` : ""}${sig.signLine ? " · signature line" : ""}` : "not marked"}</span></span>
        {repeat && (
          <span>Show the page with{" "}
            <span className={cls.segs}>{counts.map((n) => <button key={n} className={`${cls.seg} ${shown === n ? cls.segOn : ""}`} onClick={() => setShown(n)}>{n}</button>)}</span>{" "}
            buyer{shown > 1 ? "s" : ""}
          </span>
        )}
        {!repeat && <span className={cls.hint} style={{ margin: 0 }}>Buyers kept as typed (2)</span>}
        <span style={{ flex: 1 }} />
        <button className={cls.link} onClick={() => setOpen(true)}>Change buyer settings ▾</button>
      </div>
    );
  }
  return (
    <div className={cls.buyerCard}>
      <button className={cls.link} style={{ gridColumn: "1 / -1", justifySelf: "end", marginBottom: "-1rem" }} onClick={() => setOpen(false)}>Close ▴</button>
      <div>
        <span className={cls.tagBuyer}>BUYER · Allottee/s</span>
        <p className={cls.selTitle} style={{ margin: ".2rem 0 .8rem" }}>How buyers print</p>
        <div className={cls.segs} role="radiogroup" aria-label="How buyers are printed">
          {[["REPEAT", "Repeat for each buyer (1–4)"], ["KEEP_TYPED", "Keep as typed (2)"]].map(([k, t]) => (
            <button key={k} role="radio" aria-checked={card.buyerMode === k} disabled={!canEdit && card.buyerMode !== k}
              className={`${cls.seg} ${card.buyerMode === k ? cls.segOn : ""}`}
              onClick={() => canEdit && card.buyerMode !== k && onChange({ action: "MODE", buyerMode: k }, k === "REPEAT" ? "Buyers repeat for each applicant" : "Buyers kept as typed (2)")}>
              {t}
            </button>
          ))}
        </div>
        {repeat ? (
          <p className={cls.hint} style={{ marginTop: ".8rem" }}>
            Show the page with{" "}
            <span className={cls.segs}>
              {counts.map((n) => <button key={n} className={`${cls.seg} ${shown === n ? cls.segOn : ""}`} onClick={() => setShown(n)}>{n}</button>)}
            </span>{" "}
            buyer{shown > 1 ? "s" : ""}. Fill the green part once; every buyer gets it, in booking order. Preview PDF prints the same number.
          </p>
        ) : (
          <p className={cls.hint} style={{ marginTop: ".8rem" }}>Applicant 1 and Applicant 2 are mapped separately. A booking with 3 or 4 applicants cannot use this template.</p>
        )}
      </div>
      <div className={cls.blockList}>
        {KEYS.map((k) => {
          const item = itemOf(k);
          return (
            <div key={k} className={cls.blockRow}>
              <span><b>{BLOCK_NAMES[card.buyerMode][k]}</b></span>
              <span className={item ? cls.ok : cls.muted}>
                {item ? `${item.anchors.length} paragraph${item.anchors.length > 1 ? "s" : ""}` : "Not marked"}
                {item && canEdit && <> · <button className={cls.link} onClick={() => onChange({ action: "CLEAR", blockKey: k }, "Block cleared")}>Clear</button></>}
              </span>
              {item && repeat && (k === "BUYER" || k === "SIGNATURE") && (
                <span className={cls.gapPick}>
                  <label htmlFor={`gap-${k}`}>Space for each applicant (photo)</label>
                  <select id={`gap-${k}`} className={cls.sel} value={Number(item.gapLines) || 0} disabled={!canEdit}
                    onChange={(e) => onChange({ action: "GAP", blockKey: k, lines: Number(e.target.value) }, Number(e.target.value) ? `${e.target.value} empty line(s) for each applicant` : "No space for each applicant")}>
                    {Array.from({ length: 13 }, (_, n) => <option key={n} value={n}>{n ? `${n} line${n > 1 ? "s" : ""}` : "None"}</option>)}
                  </select>
                  {Number(item.gapLines) > 0 && (
                    <select aria-label="Where the space goes" className={cls.sel} value={item.gapWhere === "BELOW" ? "BELOW" : "ABOVE"} disabled={!canEdit}
                      onChange={(e) => onChange({ action: "GAP", blockKey: k, lines: Number(item.gapLines), where: e.target.value }, e.target.value === "BELOW" ? "Space below each applicant's name" : "Space above each applicant's name")}>
                      <option value="ABOVE">above the name</option>
                      <option value="BELOW">below the name</option>
                    </select>
                  )}
                </span>
              )}
              {item && repeat && k === "SIGNATURE" && (
                <span className={cls.gapPick}>
                  <label className={cls.chk} title="e.g. SIGNED AND DELIVERED / BY AND WITHINNAMED ALLOTTEES: printed once, right above the 1st applicant's name (the photo space goes above them)">
                    <input type="checkbox" checked={!!item.leadLines} disabled={!canEdit}
                      onChange={(e) => onChange({ action: "LEAD", blockKey: k, on: e.target.checked }, e.target.checked ? "The lines above print with the 1st applicant" : "The lines above print on their own again")} />
                    {" "}Lines above with the 1st applicant{item.leadLines ? ` (${item.leadLines})` : ""}
                  </label>
                  <label className={cls.chk} title="A line on the right of each applicant's line, for the signature">
                    <input type="checkbox" checked={!!item.signLine} disabled={!canEdit}
                      onChange={(e) => onChange({ action: "SIGNLINE", blockKey: k, on: e.target.checked }, e.target.checked ? "Signature line on the right of each applicant" : "No signature line")} />
                    {" "}Signature line on the right
                  </label>
                </span>
              )}
            </div>
          );
        })}
        <p className={cls.hint} style={{ margin: 0 }}>
          To mark one: click its line on the page (or select several lines), then choose what it is on the right.
          Buyer fields (name, age, PAN …) go inside the buyer details.
        </p>
      </div>
    </div>
  );
}

export default BuyerCard;
